import { useEffect, useState } from 'react';
import { useOnboardingAnchors } from './anchorContext';
import type { Beat } from './steps';

/** How long the ring rests on a control before moving to the next one. */
export const DWELL_MS = 900;

/**
 * How long a beat is given to produce its control before the walk gives up on
 * it. A beat asks for its panel on arrival and the control inside appears a
 * frame or two later, so treating "not there yet" as "not coming" skipped the
 * tab-rail beat every time and jumped straight to the panel.
 */
const GRACE_MS = 450;

/**
 * Walks a card's path one beat at a time, so reaching a panel is shown as the
 * route the author would have taken: press Customize, pick Sources from the tab
 * rail, land on Add source.
 *
 * A beat's surface is applied the moment it is reached, before its anchor can
 * register, because opening that panel is usually what puts the next control on
 * screen. The walk then waits for the control to appear rather than resting on
 * an empty space.
 *
 * Nothing to rest on means nothing to wait for: a beat whose control never
 * arrives, or has already been crossfaded out, is stepped over immediately. That
 * is what keeps the second and third Customize cards from pausing on a header
 * that is inert because the card before them already opened customize.
 */
export const useStepBeats = (
  beats: Beat[],
  stepKey: string,
  isRunning: boolean
) => {
  const registry = useOnboardingAnchors();
  // The index is stored with the card it was counted for, and read back as zero
  // the moment they disagree. Resetting through an effect instead left one
  // commit where the new card was read at the old card's index, and the surface
  // effect ran in that commit: stepping off a card's second beat opened
  // whatever the next card's second beat asked for, then undid it.
  const [walk, setWalk] = useState({ stepKey, index: 0 });
  const index = walk.stepKey === stepKey ? walk.index : 0;

  const safeIndex = Math.min(index, Math.max(beats.length - 1, 0));
  const current = beats[safeIndex];
  const isLast = safeIndex >= beats.length - 1;
  const isCurrentReady = Boolean(
    current && registry?.getAnchor(current.anchor)
  );
  const version = registry?.version;

  useEffect(() => {
    // Hold at the opening beat while the guide is not on screen. The flag and
    // the profile both take a moment to resolve, and a walk that ran through
    // that window handed the author a card already at its last beat: the ring
    // never visited the control the card opens with, and Next was live
    // immediately.
    if (!isRunning) {
      if (safeIndex !== 0) setWalk({ stepKey, index: 0 });
      return;
    }

    if (isLast) return;

    const advance = () => setWalk({ stepKey, index: safeIndex + 1 });
    // Registering re-runs this through `version`, so a control that arrives
    // inside the grace window is picked up and gets its full rest.
    const timer = setTimeout(advance, isCurrentReady ? DWELL_MS : GRACE_MS);

    return () => clearTimeout(timer);
  }, [stepKey, safeIndex, isLast, isCurrentReady, version, isRunning]);

  const restingBeat =
    isLast && !isCurrentReady && safeIndex > 0 ? beats[safeIndex - 1] : current;

  return {
    anchor: restingBeat?.anchor ?? null,
    surface: current?.surface ?? null,
    // Still on its way to the last beat. Every beat before the last is on a
    // timer that always fires, so this cannot stay true.
    isWalking: !isLast,
    // A beat with nothing to point at dims nothing. The Style card follows the
    // Views card with customize already open, so its first two beats find no
    // Customize button and no home card and are stepped over: dimming the page
    // around a ring that is not being drawn just flickered the overlay on the
    // way between two panels.
    scrim: isCurrentReady ? (current?.scrim ?? 'spotlight') : 'none',
  };
};
